import React, { useState, useEffect, Suspense, lazy } from 'react';
import { useAuth } from '../../../../context/AuthContext';
import { Helmet } from 'react-helmet-async';
import { getAllDiscussions } from '../../../../services/student/forumService';
import type { IDiscussion } from '../../../../types/forum';
import Loader from '../../../../components/common/Loader';
import { showToast } from '../../../../utils/showToast';
import { Plus, MessageSquare, ChevronDown, ChevronUp } from 'lucide-react';
import ReplyForm from './forum/ReplyForm'; // A new, dedicated component for replies

const NewDiscussionModal = lazy(() => import('./forum/NewDiscussionModal'));

const ForumDiscussion: React.FC = () => {
    const { user, isLoading: isAuthLoading } = useAuth();
    const [discussions, setDiscussions] = useState<IDiscussion[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [expandedDiscussionId, setExpandedDiscussionId] = useState<string | null>(null);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

    const fetchDiscussions = async () => {
        setIsLoading(true);
        try {
            const data = await getAllDiscussions();
            setDiscussions(data);
        } catch (err: any) {
            showToast(err.message, 'error');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchDiscussions();
    }, []);

    const onDiscussionCreated = (newDiscussion: IDiscussion) => {
        setDiscussions(prev => [newDiscussion, ...prev]);
        fetchDiscussions(); // Optionally refetch all to ensure consistency
    };

    const onReplyAdded = (discussionId: string, newReply: IDiscussion['replies'][0]) => {
        setDiscussions(prev => prev.map(discussion =>
            discussion._id === discussionId
                ? { ...discussion, replies: [...discussion.replies, newReply] }
                : discussion
        ));
    };

    const toggleDiscussionExpansion = (discussionId: string) => {
        setExpandedDiscussionId(prevId => (prevId === discussionId ? null : discussionId));
    };

    if (isAuthLoading || isLoading) {
        return <Loader />;
    }

    return (
        <>
            <Helmet>
                <title>Student Forum | ShreeGuruCool</title>
                <meta name="description" content="Join the ShreeGuruCool student forum. Ask questions, share knowledge, and connect with fellow SAP learners and mentors." />
                <meta name="keywords" content="student forum, SAP community, SAP SD questions, SAP MM forum, S/4HANA discussion" />
                <meta property="og:title" content="Student Forum | ShreeGuruCool" />
                <meta property="og:description" content="Connect with the ShreeGuruCool community. Discuss topics, solve problems, and enhance your SAP learning journey." />
                {/* Add other meta tags like og:image */}
            </Helmet>
            
            <div className="max-w-4xl mx-auto py-10 px-4 space-y-6">
                <header className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200">
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-800">Student Forum</h1>
                            <p className="text-gray-500 mt-1">Ask questions, share insights, and learn together.</p>
                        </div>
                        {user && (
                             <button onClick={() => setIsCreateModalOpen(true)} className="w-full md:w-auto bg-blue-600 text-white px-4 py-2.5 rounded-full text-sm font-medium hover:bg-blue-700 flex items-center justify-center gap-2">
                                <Plus size={16} /> Start a New Discussion
                            </button>
                        )}
                    </div>
                </header>

                {discussions.length === 0 ? (
                    <div className="text-center py-16 bg-white rounded-2xl shadow-sm border">
                        <MessageSquare className="mx-auto h-12 w-12 text-gray-400" />
                        <h3 className="mt-2 text-lg font-medium text-gray-900">No Discussions Yet</h3>
                        <p className="mt-1 text-sm text-gray-500">Be the first to start a conversation!</p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {discussions.map(discussion => (
                            <div key={discussion._id} className="bg-white rounded-2xl shadow-sm border border-gray-200">
                                <div
                                    className="p-5 cursor-pointer hover:bg-gray-50 transition-colors"
                                    onClick={() => toggleDiscussionExpansion(discussion._id)}
                                >
                                    <div className="flex justify-between items-start">
                                        <h2 className="text-xl font-bold text-gray-800">{discussion.title}</h2>
                                        {expandedDiscussionId === discussion._id ? <ChevronUp className="text-gray-500"/> : <ChevronDown className="text-gray-500"/>}
                                    </div>
                                    <div className="flex items-center gap-4 text-sm text-gray-500 mt-2">
                                        <span>By {discussion.authorName}</span>
                                        <span className="bg-gray-100 px-2 py-0.5 rounded-md">{discussion.category}</span>
                                        <span>{new Date(discussion.createdAt).toLocaleDateString()}</span>
                                        <span>{discussion.replies.length} replies</span>
                                    </div>
                                </div>

                                {expandedDiscussionId === discussion._id && (
                                    <div className="p-5 border-t border-gray-200 space-y-4">
                                        <p className="text-gray-700 whitespace-pre-line">{discussion.content}</p>
                                        
                                        {discussion.replies && discussion.replies.length > 0 && (
                                            <div className="border-t pt-4 space-y-3">
                                                {discussion.replies.map((reply, replyIndex) => (
                                                    <div key={replyIndex} className="bg-gray-50 p-3 rounded-lg border">
                                                        <p className="text-sm text-gray-700 whitespace-pre-line">{reply.content}</p>
                                                        <p className="text-xs text-gray-500 mt-2">By {reply.authorName} • {new Date(reply.createdAt).toLocaleDateString()}</p>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        {user && <ReplyForm discussionId={discussion._id} onReplyAdded={onReplyAdded} />}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <Suspense fallback={null}>
                {isCreateModalOpen && (
                    <NewDiscussionModal
                        isOpen={isCreateModalOpen}
                        onClose={() => setIsCreateModalOpen(false)}
                        onSuccess={onDiscussionCreated}
                    />
                )}
            </Suspense>
        </>
    );
};

export default ForumDiscussion;